import * as d3 from 'd3';

import { ChartWithAxesBase } from './ChartWithAxesBase';
import { IChartOptions } from '../../models/IChartOptions';
import { SvgManager } from '../SvgManager';
import { RoundingHelper } from '../../helpers/helpers';
import { infoTextColor } from '../../../common/constants';

export class HorizontalBarChart extends ChartWithAxesBase {
    private _rectHeight: number;
    private _crosshairGroup: d3.Selection<any>;
    private _labels: string[];
    private _data: any[];
    private _crosshairHeight: number;
    private _crosshairWidth: number;
    private _format = d3.format('.2f');
    private _valueFunction: (d: any, i?: number) => number;
    private _seriesClickFunction: (d: any, i?: number) => void;

    constructor(options: IChartOptions, svgManager: SvgManager) {
        super(options, svgManager, 'horizontal-bar-chart');
        this._labels = [];
        this._crosshairHeight = 30;
        this._crosshairWidth = 120;
        this.initLocal(svgManager);
        this.titleText('A bar chart');
        this.isTitleVisible(true);
        this.isLegendVisible(false);
        this.valueFunction(d => d.value);
        this.seriesClick(() => { return; });
    }

    private initLocal(svgManager) {
        const self = this;
        this.xAxis().tickFormat((d) => { return self._format(d); });
        this.yAxis().tickFormat(d => self.labels()[d]);

        this._crosshairGroup = this.plotAreaGroup().append('g')
            .style('visibility', 'hidden');
        this._crosshairGroup.append('rect')
            .attr({
                'width': this._crosshairWidth,
                'height': this._crosshairHeight
            })
            .classed('crosshair-text-group', true);
        this._crosshairGroup.append('text')
            .classed('crosshair-text', true)
            .attr({
                'x': this._crosshairWidth - 2,
                'y': 20
            });

        this.resizeFunction(this.applyResize());
    }

    private applyResize() {
        const self = this;
        return () => {
            self.executeHandled(() => {
                self.resizeAxes();
                self._rectHeight = Math.min(self.plotAreaHeight() / (self.labels().length) * 0.6, 20);
                self.translate();
                if (self._data) {
                    self.update(self._data);
                }
            });
        };
    }

    private _labelFunction(d) {
        return d.status;
    }

    //private _valueFunction(d) {
    //    return d.value;
    //}

    private mouseEnter(self: this) {
        return () => {
            const target = (<Event>d3.event).target;
            const rect = d3.select(target);
            rect.classed('highlight', true);
            self._crosshairGroup.style('visibility', 'visible');
        };
    }

    private mouseMove(self: this) {
        return (d, i) => {
            const target = (<Event>d3.event).target;
            const mouse = d3.mouse(target);
            //const translateX = (mouse[0] + self.translateX() + 5);
            const translateX = (mouse[0]);
            const translateY = (mouse[1]);
            self._crosshairGroup.attr({
                'transform': 'translate(' + translateX + ',' + translateY + ')'
            });
            const label = self.labels()[i];
            self._crosshairGroup.select('text').text(label + ': ' + self._format(self._valueFunction(d)));
        };
    }


    valueFunction(value?: (d, i?) => number): (d, i?) => number {
        if (value) {
            this._valueFunction = value;
        }
        return this._valueFunction;
    }

    seriesClick(value?: (d, i?) => void): (d, i?) => void {
        if (value) {
            this._seriesClickFunction = value;
        }
        return this._seriesClickFunction;
    }

    labels(value?: string[]): string[] {
        if (value && value.length > 0) {
            this._labels = value;
            const scale = this.yAxis().scale();
            scale.domain([-0.5, this.labels().length - 0.5]);
            this.yAxis().tickvalues(d3.range(0, this.labels().length, 1));
            const width = <number>d3.max(value, v => v.length) * 80 / 11;
            this.yAxis().width(width);
            this._rectHeight = this.plotAreaHeight() / (this.labels().length) * 0.6;
            this.applyResize()();
            if (this._data && this._data.length > 0) {
                this.update(this._data);
            }
        }
        return this._labels;
    }

    update(data) {
        this._data = data;
        if (data) {
            const min = data.length > 0 ? d3.min(this._data, d => this._valueFunction(d)) : 0;
            const max = data.length > 0 ? d3.max(this._data, d => this._valueFunction(d)) : 1;
            const roundingHelper = new RoundingHelper();
            const rounded = roundingHelper.round(<number>min, <number>max);
            this.max(rounded.max);
            const rectangles = this.seriesGroup()
                .selectAll('rect')
                .data(data);
            const self = this;
            rectangles.enter()
                .append('rect')
                .on('mouseenter', self.mouseEnter(self))
                .on('mousemove', self.mouseMove(self))
                .on('mouseout', () => {
                    d3.select((<Event>d3.event).target).classed('highlight', false);
                    self._crosshairGroup.style('visibility', 'hidden');
                })
                .on('click', (d, i) => self.seriesClick()(d, i));
            rectangles.attr({
                'x': 0,
                'y': (d, i) => {
                    const y = (self.yAxis().scale()(self._labelFunction(d)));
                    return y;
                },
                'width': (d, i) => { return self.xAxis().scale()(self._valueFunction(d)); },
                'height': self._rectHeight,
                'fill': (d) => {
                    const color = infoTextColor;
                    return color;
                },
                'transform': 'translate(' + 0 + ',' + (-self._rectHeight / 2) + ')'
            });
            rectangles.exit().remove();
        }
    }

    max(value) {
        this.updateXAxis(0, value);
    }

    numberFormat(value: string): void {
        this._format = d3.format(value);
        this.displayOptions({ xAxisFormat: value });
        //const self = this;
        //this.xAxis().tickFormat(d=>self._format(d));
    }
}